import { useEffect, useState } from 'react';
import { Modal, Button } from '@shared-ui/components';
import './CopyPipelineDialog.css';

export interface CopyPipelineDialogProps {
  isOpen: boolean;
  /** Source pipeline being cloned — shown to the admin for confirmation. */
  sourcePipelineName: string;
  /** Human-readable operation label (e.g., "Solution Analysis"). */
  opLabel: string;
  /** Names already in use within the operation — used to block duplicates. */
  existingNames: Set<string>;
  /** Pre-fill for the input. Typically `<source>-copy` (auto-suffixed when
   *  the basic suggestion is taken). */
  suggestedName: string;
  onClose: () => void;
  onConfirm: (name: string) => void;
}

/**
 * Theme-aware "Copy pipeline" prompt. Replaces `window.prompt` with a
 * Modal that matches the admin console's dark theme. Owns its own draft +
 * error state; the parent only supplies the suggested name and the
 * uniqueness contract (`existingNames`), and receives the validated final
 * name in `onConfirm`.
 */
export function CopyPipelineDialog({
  isOpen,
  sourcePipelineName,
  opLabel,
  existingNames,
  suggestedName,
  onClose,
  onConfirm,
}: CopyPipelineDialogProps) {
  const [draftName, setDraftName] = useState(suggestedName);
  const [error, setError] = useState<string | undefined>(undefined);

  // Reset draft + error each time the dialog (re-)opens with a new source.
  useEffect(() => {
    if (isOpen) {
      setDraftName(suggestedName);
      setError(undefined);
    }
  }, [isOpen, suggestedName]);

  if (!isOpen) return null;

  const submit = () => {
    const name = draftName.trim();
    if (!name) {
      setError('Pipeline name cannot be empty.');
      return;
    }
    if (existingNames.has(name)) {
      setError(`A pipeline named "${name}" already exists.`);
      return;
    }
    onConfirm(name);
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Copy pipeline" size="sm">
      <div className="copy-pipeline-dialog">
        <p className="copy-pipeline-source">
          Copying <code>{sourcePipelineName}</code> in <strong>{opLabel}</strong>.
        </p>
        <label className="copy-pipeline-field">
          <span>New pipeline name</span>
          <input
            type="text"
            value={draftName}
            autoFocus
            onChange={(e) => { setDraftName(e.target.value); if (error) setError(undefined); }}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          />
        </label>
        <p className="copy-pipeline-hint">
          Must be unique within {opLabel}. The copy starts disabled so you can retarget
          {' '}(e.g. <code>executionModes: ['background']</code>) before activating it.
        </p>
        {error && <p className="copy-pipeline-error">{error}</p>}
        <div className="copy-pipeline-actions">
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={submit} disabled={!draftName.trim()}>Copy</Button>
        </div>
      </div>
    </Modal>
  );
}
